import { useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import * as React from 'react';
import type { Role } from '@cfl/assets-shared';

import { api, ApiError } from '@/lib/api';

/**
 * Every permission the API recognises. Mirrors PERMISSIONS in
 * apps/api/src/auth/permissions.ts - the server resolves them per role and sends the
 * list, so the SPA never re-derives who may do what.
 */
export type Permission =
  | 'manageAssets'
  | 'assignAssets'
  | 'manageRepairs'
  | 'manageEmployees'
  | 'runExitClearance'
  | 'viewRegister'
  | 'viewReports'
  | 'manageSettings'
  | 'deleteRecords';

/** The signed-in employee, as resolved by GET /auth/me. */
export interface SessionUser {
  id: string;
  employeeCode: string;
  email: string | null;
  name: string;
  role: Role;
  department: string | null;
  designation: string | null;
  avatarUrl: string | null;
  /**
   * Sent by the server rather than computed here.
   *
   * IT and HR are peers with different scopes, not rungs on a ladder, so there is no
   * "at least" comparison the browser could make. Reading the server's answer means
   * adding a permission does not need the SPA redeployed in step, and the two can
   * never disagree about who may do what.
   */
  permissions: Permission[];
  /** Assets currently issued to this person. Drives the "My assets" nav badge. */
  assetsHeld: number;
}

export type SessionState =
  | { status: 'loading' }
  | { status: 'unauthenticated' }
  /** Employee record is EXITED. Credentials are valid, so the login screen would loop. */
  | { status: 'disabled' }
  /** Signed in to Google, but HR has never created an employee record. */
  | { status: 'not-onboarded' }
  | { status: 'authenticated'; user: SessionUser };

const SessionContext = React.createContext<SessionState | null>(null);

export const SESSION_QUERY_KEY = ['auth', 'me'] as const;

function useSessionQuery(): UseQueryResult<SessionUser, ApiError> {
  return useQuery<SessionUser, ApiError>({
    queryKey: SESSION_QUERY_KEY,
    queryFn: () => api.get<SessionUser>('/auth/me'),
    // A 401 is a real answer ("not signed in"), not a transient failure worth retrying.
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status >= 400 && error.status < 500) &&
      failureCount < 2,
    staleTime: 60_000,
  });
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const query = useSessionQuery();

  const value = React.useMemo<SessionState>(() => {
    if (query.isPending) return { status: 'loading' };
    if (query.data) return { status: 'authenticated', user: query.data };

    const error = query.error;
    if (error instanceof ApiError) {
      if (error.isAccountDisabled) return { status: 'disabled' };
      // Distinct from unauthenticated: their Google sign-in worked, so bouncing them
      // to the login screen would just loop. They need HR, not another attempt.
      if (error.code === 'NOT_ONBOARDED') return { status: 'not-onboarded' };
    }
    return { status: 'unauthenticated' };
  }, [query.isPending, query.data, query.error]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = React.useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>');
  return ctx;
}

/** The signed-in user, for a component that already sits behind <RequireAuth>. */
export function useCurrentUser(): SessionUser {
  const session = useSession();
  if (session.status !== 'authenticated') {
    throw new Error('useCurrentUser called outside an authenticated route');
  }
  return session.user;
}

/**
 * Whether the signed-in user holds a permission.
 *
 * Used to hide a button, never to protect anything: the API re-checks every one of
 * these on the route. A hidden button the user could still reach by URL is a tidiness
 * problem; an unchecked route is a security one.
 */
export function useCan(permission: Permission): boolean {
  const session = useSession();
  return session.status === 'authenticated' && session.user.permissions.includes(permission);
}

/** Sign out, then clear the cache so no screen keeps rendering the last user's data. */
export function useSignOut(): () => Promise<void> {
  const queryClient = useQueryClient();
  return React.useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      // In `finally`: if the logout request failed the cookie may still be gone, and
      // leaving a stale cache on screen is the worse of the two outcomes.
      queryClient.clear();
      window.location.assign('/login');
    }
  }, [queryClient]);
}
